import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Locations, type StorageLocationKind } from '@/lib/api';
import { Button, Modal, Input, RadioGroup, Select } from '@/design-system';
import { useToasts } from '@/lib/store';
import { flattenLocationTree, LOCATION_KINDS } from '@/lib/locations';

const KIND_LABEL: Record<StorageLocationKind, string> = {
  room: 'Rom',
  cabinet: 'Skap',
  fridge: 'Kjøleskap',
  shelf: 'Hylle',
  freezer: 'Fryser',
  other: 'Annet',
};

interface LocationFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Pre-selects this as the parent (e.g. "Ny lokasjon" clicked inside a Room row). */
  defaultParentId?: string;
  onCreated?: (locationId: string) => void;
}

/** "Ny lokasjon" — name + kind + optional parent, enforcing the 3-level cap server-side. */
export function LocationFormModal({ open, onClose, defaultParentId, onCreated }: LocationFormModalProps) {
  const qc = useQueryClient();
  const { push } = useToasts();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<StorageLocationKind>('room');
  const [parentId, setParentId] = useState<string | undefined>(defaultParentId);

  const treeQ = useQuery({ queryKey: ['locations.tree'], queryFn: () => Locations.tree(), enabled: open });
  const parentOptions = flattenLocationTree(treeQ.data?.tree ?? []);

  useEffect(() => {
    if (open) {
      setName('');
      setKind(defaultParentId ? 'shelf' : 'room');
      setParentId(defaultParentId);
    }
  }, [open, defaultParentId]);

  const create = useMutation({
    mutationFn: () => Locations.create({ name: name.trim(), kind, parentId }),
    onSuccess: (location) => {
      push({ title: 'Lokasjon opprettet', description: location.name, tone: 'success' });
      void qc.invalidateQueries({ queryKey: ['locations.tree'] });
      void qc.invalidateQueries({ queryKey: ['locations.list'] });
      onCreated?.(location.id);
      onClose();
    },
    onError: (err: Error) => push({ title: 'Feil', description: err.message, tone: 'danger' }),
  });

  return (
    <Modal open={open} onClose={onClose} title="Ny lokasjon" size="sm">
      <div className="grid gap-4">
        <Input label="Navn" placeholder="F.eks. Kjøleskap 2" value={name} onChange={(e) => setName(e.target.value)} autoFocus />

        <div className="grid gap-1.5">
          <span className="text-xs font-medium uppercase tracking-wider text-mist">Type</span>
          <RadioGroup
            name="location-kind"
            value={kind}
            onChange={(v) => setKind(v as StorageLocationKind)}
            options={LOCATION_KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
          />
        </div>

        <Select
          label="Ligger i (valgfritt)"
          value={parentId ?? ''}
          onChange={(e) => setParentId(e.target.value || undefined)}
          options={[
            { value: '', label: 'Ingen — toppnivå (Rom)' },
            ...parentOptions.map((opt) => ({ value: opt.id, label: `${'› '.repeat(opt.depth)}${opt.name}` })),
          ]}
        />
        <p className="text-xs text-mist">Maks 3 nivåer dypt: Rom → Skap/Kjøleskap → Hylle.</p>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Avbryt</Button>
          <Button
            variant="primary"
            iconLeft="check"
            loading={create.isPending}
            disabled={!name.trim()}
            onClick={() => create.mutate()}
          >
            Opprett
          </Button>
        </div>
      </div>
    </Modal>
  );
}
